import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, C } from "@/components/district/kit";
import { ChartBar, CircleNotch } from "@phosphor-icons/react";

const usd = (n) => "$" + Math.round(Number(n) || 0).toLocaleString();
const BAND_COLORS = ["#8FA0F5", "#6E7FF0", "#5A3FE0", "#B3175A", "#B07D10", "#0E8C99", "#0F8A5F"];

// Donor distribution by lifetime-giving band. Used on both the district dashboard
// (aggregated) and the club dashboard (single club). `endpoint` decides the scope.
export const GivingBandsCard = ({ endpoint, subtitle }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(endpoint).then((r) => on && setD(r.data)).catch(() => on && setD({ order: [], bands: {}, total: 0, total_donors: 0 }));
    return () => { on = false; };
  }, [endpoint]);

  if (!d) return (
    <Card data-testid="giving-bands-card" className="flex h-40 items-center justify-center">
      <CircleNotch size={26} className="animate-spin" style={{ color: C.indigo }} />
    </Card>
  );

  const order = d.order || [];
  const maxDon = Math.max(1, ...order.map((b) => (d.bands[b]?.donors) || 0));
  const empty = (d.total_donors || 0) === 0;

  return (
    <Card data-testid="giving-bands-card" className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <ChartBar size={22} weight="fill" style={{ color: C.indigo }} />
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>Donors by Giving Band</h2>
          <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{subtitle || "Total giving by donor band"}</p>
        </div>
      </div>

      {empty ? (
        <p className="mt-6 py-6 text-center text-[14px] font-medium" style={{ color: C.sub }}>No giving data uploaded yet.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2 text-[13px] font-semibold">
            <span className="rounded-lg px-3 py-1.5" style={{ background: C.indigoSoft, color: C.indigoText }} data-testid="bands-total-donors">{(d.total_donors || 0).toLocaleString()} donors</span>
            <span className="rounded-lg px-3 py-1.5" style={{ background: C.indigoSoft, color: C.indigoText }} data-testid="bands-total-amount">{usd(d.total)}</span>
          </div>
          <div className="mt-4 space-y-2.5">
            {order.map((b, i) => {
              const row = d.bands[b] || { amount: 0, donors: 0 };
              const pct = Math.round(((row.donors || 0) / maxDon) * 100);
              return (
                <div key={b} data-testid={`band-row-${i}`}>
                  <div className="flex items-baseline justify-between gap-2 text-[12.5px]">
                    <span className="font-semibold" style={{ color: C.ink }}>{b}</span>
                    <span className="shrink-0 font-bold" style={{ color: C.ink }}>{(row.donors || 0).toLocaleString()}<span className="ml-1 font-semibold" style={{ color: C.sub }}>donors</span></span>
                  </div>
                  <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full" style={{ background: "#EFEEF9" }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(pct, row.donors ? 3 : 0)}%`, background: BAND_COLORS[i % BAND_COLORS.length] }} />
                  </div>
                  <p className="mt-0.5 text-[11.5px] font-semibold" style={{ color: C.sub }}>{usd(row.amount)}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};
